'use client';

import { useState } from 'react';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import type { PortfolioItemDetail } from '@hummii/shared-types';

interface PortfolioModalProps extends BaseModalProps {
  portfolioItems?: PortfolioItemDetail[];
  contractorName?: string;
  initialIndex?: number;
}

/**
 * Portfolio Modal - displays contractor's portfolio with image gallery
 * Usage: const { open } = useModal('portfolio'); open({ portfolioItems, contractorName });
 */
export function PortfolioModal({
  isOpen,
  onClose,
  portfolioItems = [],
  contractorName,
  initialIndex = 0,
}: PortfolioModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (portfolioItems.length === 0) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        animation="zoom"
        title="Portfolio"
      >
        <div className="text-center py-8">
          <p className="text-gray-500">No portfolio items available</p>
        </div>
      </Modal>
    );
  }

  const currentItem = portfolioItems[currentIndex];
  const currentImage = currentItem?.images[currentImageIndex] || '';

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setCurrentImageIndex(0);
    }
  };

  const handleNext = () => {
    if (currentIndex < portfolioItems.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentImageIndex(0);
    }
  };

  const handlePreviousImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  const handleNextImage = () => {
    if (currentImageIndex < currentItem.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      animation="zoom"
      title={contractorName ? `${contractorName}'s Portfolio` : 'Portfolio'}
      description={`${currentIndex + 1} of ${portfolioItems.length} projects`}
    >
      <div className="space-y-4">
        {/* Main Image */}
        <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
          {currentImage ? (
            <>
              <img
                src={currentImage}
                alt={currentItem.title}
                className="w-full h-full object-cover"
              />

              {/* Image Navigation */}
              {currentItem.images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePreviousImage}
                    disabled={currentImageIndex === 0}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Previous image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={handleNextImage}
                    disabled={currentImageIndex === currentItem.images.length - 1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    aria-label="Next image"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  {/* Image counter */}
                  <div className="absolute bottom-2 right-2 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {currentItem.images.length}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400">No image available</p>
            </div>
          )}
        </div>

        {/* Thumbnail Strip */}
        {currentItem.images.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {currentItem.images.map((image, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setCurrentImageIndex(index)}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                  index === currentImageIndex
                    ? 'border-blue-500 scale-105'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={image}
                  alt={`${currentItem.title} - ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}

        {/* Project Details */}
        <div className="pt-4 border-t border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {currentItem.title}
          </h3>
          {currentItem.description && (
            <p className="text-gray-600 whitespace-pre-wrap">
              {currentItem.description}
            </p>
          )}
          {currentItem.contractor && (
            <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-200">
              {currentItem.contractor.avatarUrl && (
                <img
                  src={currentItem.contractor.avatarUrl}
                  alt={currentItem.contractor.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {currentItem.contractor.name}
                </p>
                <p className="text-xs text-gray-500">Contractor</p>
              </div>
            </div>
          )}
        </div>

        {/* Project Navigation */}
        {portfolioItems.length > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous Project
            </button>
            <span className="text-sm text-gray-500">
              {currentIndex + 1} / {portfolioItems.length}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={currentIndex === portfolioItems.length - 1}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next Project
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
